import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { sendAudioMessage, sendImageMessage } from '../../../../services/api.js';
import { RECORDING_TICK_MS } from '../constants/timing.js';
import {
  MAX_AUDIO_BYTES,
  MAX_AUDIO_DURATION_MS,
  MAX_IMAGE_BYTES,
  SUPABASE_BUCKET_TMP_MEDIA,
  buildUuid,
  getAudioExtension,
  getImageExtension,
  pickMimeType,
} from '../utils/mediaHelpers.js';
import { supabaseMediaClient } from '../utils/supabaseMediaClient.js';

type UseChatMediaOptions = {
  phone?: string | null;
  conversationId?: string | null;
  onMediaSent?: () => Promise<void>;
  /** Troca de conversa: para gravação, limpa painéis e revoga ObjectURLs. */
  resetKey?: string | number | null;
};

export function useChatMedia({ phone, conversationId, onMediaSent, resetKey }: UseChatMediaOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [isAudioSending, setIsAudioSending] = useState(false);
  const [isAudioPanelOpen, setIsAudioPanelOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isImageSending, setIsImageSending] = useState(false);
  const [isImagePanelOpen, setIsImagePanelOpen] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const audioUrlRef = useRef<string | null>(null);
  const imageUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const discardRef = useRef(false);
  const unmountingRef = useRef(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearAudio = useCallback(() => {
    setAudioBlob(null);
    setAudioError(null);
    setRecordingMs(0);
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const clearImage = useCallback(() => {
    setImageFile(null);
    setImageError(null);
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const handleStopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.stop();
  }, []);

  const handleStartRecording = async () => {
    if (isRecording || isAudioSending) return;
    setAudioError(null);
    setIsAudioPanelOpen(true);

    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setAudioError('Seu navegador não suporta gravação de áudio.');
      return;
    }

    try {
      discardRef.current = false;
      clearAudio();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onerror = () => {
        if (!unmountingRef.current) {
          setAudioError('Erro ao gravar áudio. Tente novamente.');
        }
      };
      recorder.onstop = () => {
        stopStream();
        if (unmountingRef.current) return;

        setIsRecording(false);

        if (discardRef.current) {
          discardRef.current = false;
          chunksRef.current = [];
          return;
        }

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (!blob.size) {
          setAudioError('Não foi possível capturar áudio.');
          return;
        }

        if (blob.size > MAX_AUDIO_BYTES) {
          setAudioError('Áudio muito grande. Limite de 8MB.');
          return;
        }

        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      recorderRef.current = recorder;
      streamRef.current = stream;
      recorder.start();
      setIsRecording(true);
      setRecordingMs(0);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setAudioError('Permissão do microfone negada ou indisponível.');
      stopStream();
    }
  };

  const handleCancelAudio = () => {
    setAudioError(null);
    if (isRecording && recorderRef.current) {
      discardRef.current = true;
      handleStopRecording();
      setIsAudioPanelOpen(false);
      return;
    }
    clearAudio();
    setIsAudioPanelOpen(false);
  };

  const uploadTempAudio = async (blob: Blob) => {
    if (!supabaseMediaClient || !SUPABASE_BUCKET_TMP_MEDIA) {
      throw new Error('Supabase não configurado para upload.');
    }
    const ext = getAudioExtension(blob.type || 'audio/webm');
    const filename = `tmp-audios/${buildUuid()}.${ext}`;
    const { data, error } = await supabaseMediaClient.storage
      .from(SUPABASE_BUCKET_TMP_MEDIA)
      .upload(filename, blob, { contentType: blob.type || 'audio/webm', upsert: false });

    if (error) throw error;
    return data.path;
  };

  const handleSendAudio = async () => {
    if (!audioBlob) return;
    if (!phone) {
      setAudioError('Telefone do contato não encontrado.');
      return;
    }
    if (audioBlob.size > MAX_AUDIO_BYTES) {
      setAudioError('Áudio muito grande. Limite de 8MB.');
      return;
    }

    setIsAudioSending(true);
    setAudioError(null);

    try {
      const path = await uploadTempAudio(audioBlob);
      await sendAudioMessage({ phone, path, clienteId: conversationId });
      clearAudio();
      setIsAudioPanelOpen(false);
      try { await onMediaSent?.(); } catch (refreshError) { console.error('Erro ao atualizar mensagens:', refreshError); }
    } catch (err: any) {
      console.error('Erro ao enviar áudio:', err);
      const message = err?.response?.data?.error || err?.message || 'Erro ao enviar áudio.';
      setAudioError(message);
    } finally {
      setIsAudioSending(false);
    }
  };

  const handleImageSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError('Selecione um arquivo de imagem válido.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError('Imagem muito grande. Limite de 8MB.');
      return;
    }

    setImageError(null);
    setIsImagePanelOpen(true);
    setImageFile(file);
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handlePickPhoto = () => {
    setIsImagePanelOpen(true);
    fileInputRef.current?.click();
  };

  const uploadTempImage = async (file: File) => {
    if (!supabaseMediaClient || !SUPABASE_BUCKET_TMP_MEDIA) {
      throw new Error('Supabase não configurado para upload.');
    }
    const ext = getImageExtension(file);
    const filename = `tmp-images/${buildUuid()}.${ext}`;
    const { data, error } = await supabaseMediaClient.storage
      .from(SUPABASE_BUCKET_TMP_MEDIA)
      .upload(filename, file, { contentType: file.type || 'image/jpeg', upsert: false });

    if (error) throw error;
    return data.path;
  };

  const handleSendImage = async () => {
    if (!imageFile) return;
    if (!phone) {
      setImageError('Telefone do contato não encontrado.');
      return;
    }
    if (imageFile.size > MAX_IMAGE_BYTES) {
      setImageError('Imagem muito grande. Limite de 8MB.');
      return;
    }

    setIsImageSending(true);
    setImageError(null);

    try {
      const path = await uploadTempImage(imageFile);
      await sendImageMessage({ phone, path, clienteId: conversationId });
      clearImage();
      setIsImagePanelOpen(false);
      try { await onMediaSent?.(); } catch (refreshError) { console.error('Erro ao atualizar mensagens:', refreshError); }
    } catch (err: any) {
      console.error('Erro ao enviar imagem:', err);
      const message = err?.response?.data?.error || err?.message || 'Erro ao enviar imagem.';
      setImageError(message);
    } finally {
      setIsImageSending(false);
    }
  };

  const handleCancelImage = () => {
    clearImage();
    setIsImagePanelOpen(false);
  };

  const handleToggleAudioPanel = () => {
    if (isRecording) {
      handleCancelAudio();
      return;
    }
    if (audioBlob || audioError) {
      setIsAudioPanelOpen(true);
      return;
    }
    setIsAudioPanelOpen((prev) => !prev);
  };

  const handleToggleImagePanel = () => {
    if (imageFile || imageError) {
      setIsImagePanelOpen(true);
      return;
    }
    setIsImagePanelOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isRecording) return;
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setRecordingMs(elapsed);
    }, RECORDING_TICK_MS);
    const timeout = window.setTimeout(() => {
      setAudioError('Gravação limitada a 60s.');
      handleStopRecording();
    }, MAX_AUDIO_DURATION_MS);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [isRecording, handleStopRecording]);

  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);

  useEffect(() => {
    imageUrlRef.current = imageUrl;
  }, [imageUrl]);

  // Reset completo ao trocar conversa (comportamento de Conversations).
  useEffect(() => {
    setAudioError(null);
    setImageError(null);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      discardRef.current = true;
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingMs(0);
    setIsRecording(false);
    setIsAudioPanelOpen(false);
    setImageFile(null);
    setImageUrl(null);
    setIsImagePanelOpen(false);
  }, [resetKey]);

  useEffect(() => {
    return () => {
      unmountingRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      stopStream();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
      }
    };
  }, [stopStream]);

  const showAudioPanel = isAudioPanelOpen || isRecording || !!audioBlob || !!audioError;
  const showImagePanel = isImagePanelOpen || !!imageFile || !!imageError || isImageSending;

  return {
    // audio
    isRecording,
    recordingMs,
    audioBlob,
    audioUrl,
    audioError,
    isAudioSending,
    isAudioPanelOpen,
    showAudioPanel,
    handleStartRecording,
    handleStopRecording,
    handleCancelAudio,
    handleSendAudio,
    handleToggleAudioPanel,
    // image
    imageFile,
    imageUrl,
    imageError,
    isImageSending,
    isImagePanelOpen,
    showImagePanel,
    fileInputRef,
    handleImageSelected,
    handlePickPhoto,
    handleSendImage,
    handleCancelImage,
    handleToggleImagePanel,
  };
}
